import type { ParseKeys } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { isDeferredSettlement } from "../utils/deferredSettlement";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  TrashIcon,
  ArrowLeftIcon,
  XMarkIcon,
  PlusIcon,
} from "@heroicons/react/24/solid";
import { CameraIcon, StopIcon } from "@heroicons/react/24/outline";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/Button";
import { AppleComboBox } from "../components/AppleComboBox";
import { apiGet, apiPost } from "../api/client";
import { saveDraft, loadDraft, clearDraft } from "../utils/localStorageDraft";
import {
  decideSaleSubmissionIdentity,
  fingerprintSalePayload,
  getOrCreateIdempotencyKey,
  randomUuidKey,
  isLegacySaleFingerprint,
} from "../utils/saleIdempotencyKey";
import {
  isCashPaymentMethod,
  paymentMethodFeePercent,
  paymentMethodFeeFraction,
  paymentMethodFixedFee,
} from "../utils/paymentMethod";
import {
  resolveSaleFeeFields,
  SaleLinePostBody,
  SalePostBody,
  SalePostRequest,
} from "../utils/salePosting";
import {
  FeeMode,
  initFeeIntentFromDraft,
  reduceOnAutoCalc,
  reduceOnUserFeeEdit,
  reduceOnUserAmountOrMethodChange,
} from "../utils/saleFeeHydration";
import {
  readLastPaymentMethod,
  writeLastPaymentMethod,
} from "../utils/lastPaymentMethod";
import { companyRoute } from "../utils/routeHelpers";
import { normalizeIndustry, type IndustryKey } from "../lib/industry";
import type { AuthMe } from "../types/auth";
import UpgradeModal from "../components/UpgradeModal";
import { formatAnyDateToLocale, ymdTodayLocal } from "../utils/dateUtils";
import { formatNumber, formatMoney } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";
import MemoSection from "../components/MemoSection";
import { AppleDateInput } from "../components/AppleDateInput";
import { AppleNumberInput } from "../components/AppleNumberInput";
import CustomerEntry from "./CustomerEntry";
import ProductEntry from "./ProductEntry";
import { usePostHog } from "@posthog/react";
import { QRCodeSVG } from "qrcode.react";
import {
  extractApiDetailOutcome,
  formatClosedThroughDate,
  isClosedPeriodError,
  isDateClosed,
} from "../utils/closedPeriod";
const SALE_DRAFT_KEY = "ricebook_saleentry_draft";
const STRIPE_SYNTH = "__STRIPE__";

// ---------- Types ----------
type Line = {
  product_id: string;
  detail: string;
  qty: string;
  price: string;
  discount: string;
  total: string;
  lastEdited?: "price" | "discount" | "total";
  item_kind?: "product" | "service";
};

type Product = {
  id: number | string;
  name: string;
  stock_qty?: number;
  sell_price?: number;
  is_service?: boolean;
  barcode?: string;
  sku?: string;
  product_code?: string;
};

type Customer = {
  id: number | string;
  name: string;
  email?: string;
  mobile?: string;
  phone?: string;
};

type InvoiceSendoff = {
  public_url?: string;
  sequence?: string[];
  launch_channel?: "whatsapp" | "email" | null;
  launch_url?: string | null;
  fallback_channel?: "whatsapp" | "email" | null;
  customer_has_email?: boolean;
  customer_has_mobile?: boolean;
};

type PaymentMethod = {
  id: number;
  name: string;
  type: string;
  // FEE-RATE-SEMANTICS-1 Phase 2B: read the backend-derived effective percent,
  // never the raw fee_rate. Previews divide by 100 exactly once and are
  // non-authoritative (configured posting sends no computed fee).
  fee_rate_percent?: number | null;
  fixed_fee?: number;
  account_id: number;
  provider?: string;
  provider_method?: "card" | "alipay" | "wechat_pay" | string;
  online?: boolean;
};

type Account = {
  id: number | string;
  name: string;
  type?: string | null;
  kind?: string | null;
  is_system?: boolean | null;
};

// FEE-RATE-SEMANTICS-1 Phase 2B: POST /sales body types are the SHARED, typed
// definitions in utils/salePosting.ts (also exercised by the compile-time gate).

const lower = (s?: string | null) => (s || "").toLowerCase();

function isValidSalePaymentAccount(account?: Account | null) {
  if (!account || account.is_system) return false;

  const type = lower(account.type);
  const kind = lower(account.kind);

  return (
    type === "asset" &&
    ["cash", "bank", "payment", "payment_gateway", "gateway"].includes(kind)
  );
}

const defaultLine: Line = {
  product_id: "",
  detail: "",
  qty: "1",
  price: "0",
  discount: "0",
  total: "0",
  item_kind: undefined,
  lastEdited: undefined,
};

// helpers


const isStripePM = (pm?: PaymentMethod | null) =>
  !!pm && pm.provider === "stripe" && pm.online === true;

// FEE-RATE-SEMANTICS-1 Phase 2B: shared exact cash classifier (also used by
// SaleDetail) so the two surfaces cannot drift.
const isPMCash = (pm?: PaymentMethod | null) => isCashPaymentMethod(pm);

function getLineTotal(qty: string, price: string, discount: string) {
  const q = parseFloat(qty) || 1;
  const p = parseFloat(price) || 0;
  const d = parseFloat(discount) || 0;
  const n = Math.round(q * p * (1 - d / 100) * 100) / 100;
  if (!Number.isFinite(n)) return "0";
  return String(n);
}

function getPreorderStatus(lines: Line[], products: Product[]) {
  return lines.map((line) => {
    if (!line.product_id) return false;
    const prod = products.find((p) => String(p.id) === line.product_id);
    if (prod?.is_service) return false;
    if (!prod || typeof prod.stock_qty !== "number") return false;
    return parseFloat(line.qty) > prod.stock_qty;
  });
}

function pmWeight(pm: PaymentMethod) {
  const name = (pm.name || "").toLowerCase();
  const type = (pm.type || "").toLowerCase();

  if (name.includes("cash")) return 10;

  if (
    name.includes("fps") ||
    name.includes("bank") ||
    name.includes("transfer") ||
    name.includes("payme")
  )
    return 20;

  if (
    name.includes("visa") ||
    name.includes("master") ||
    name.includes("mastercard") ||
    name.includes("amex") ||
    name.includes("american express") ||
    name.includes("card")
  )
    return 30;

  if (name.includes("wechat")) return 40;
  if (name.includes("alipay")) return 50;

  if (isDeferredSettlement(pm.type)) return 90;

  return 80;
}

function asUserMessage(err: any): SalesMessage {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d: any) => d?.msg || (d?.type ? String(d.type) : null))
      .filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  }
  if (detail && typeof detail === "object" && (detail.message || detail.msg))
    return String(detail.message || detail.msg);
  return (err instanceof LocalSalesError ? err.display : err?.message) || localMessage("sales.errors.generic");
}

function parseMoneyInput(v?: string | number | null) {
  const s = String(v ?? "")
    .replace(/,/g, "")
    .trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function toInputMoney(n: number) {
  const safe = Number.isFinite(n) ? n : 0;
  return String(Math.round(safe * 100) / 100);
}

function getProductName(product: Product) {
  return product.name || "Item";
}

function getProductSku(product: Product) {
  return (product.sku || "").trim();
}

function renderProductOption(product: Product, displayLabel: string, skuLabel: string) {
  const sku = getProductSku(product);

  return (
    <span className="block min-w-0">
      <span className="block text-[13px] leading-5 text-gray-700">
        {displayLabel}
      </span>
      {sku && (
        <span className="block text-[12px] leading-4 text-gray-500">
          {skuLabel}
        </span>
      )}
    </span>
  );
}

function filterProductOptions(options: Product[], query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return options;

  return options
    .map((product, index) => {
      const sku = getProductSku(product).toLowerCase();
      const name = getProductName(product).toLowerCase();

      let rank = Number.POSITIVE_INFINITY;
      if (sku && sku === q) rank = 0;
      else if (sku && sku.startsWith(q)) rank = 1;
      else if (name.startsWith(q)) rank = 2;
      else if (sku && sku.includes(q)) rank = 3;
      else if (name.includes(q)) rank = 4;

      return { product, index, rank };
    })
    .filter((row) => Number.isFinite(row.rank))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((row) => row.product);
}

type LocalSalesMessage = { key: ParseKeys; values?: Record<string, string | number>; closedDate?: string | null };
type SalesMessage = string | LocalSalesMessage;
const localMessage = (key: ParseKeys, values?: Record<string, string | number>): LocalSalesMessage => ({ key, values });
class LocalSalesError extends Error {
  constructor(readonly display: LocalSalesMessage) { super(display.key); }
}
function localApiDetail(err: any): SalesMessage {
  if (err instanceof LocalSalesError) return err.display;
  const outcome = extractApiDetailOutcome(err);
  return outcome.kind === "raw" ? outcome.text : localMessage("sales.errors.generic");
}
function localClosedPeriod(closedDate?: string | null): LocalSalesMessage {
  return { key: "sales.errors.closedSubmit", closedDate };
}

export default function SaleEntry() {
  const { t } = useTranslation();
  const displayProductOptionLabel = (product: Product) => product.id === "" ? t("sales.entry.serviceOption") : product.name || t("sales.entry.itemFallback");
  const messageText = (message: SalesMessage | null): string => {
    if (message == null) return "";
    if (typeof message === "string") return message;
    const values = "closedDate" in message
      ? { date: message.closedDate ? formatClosedThroughDate(message.closedDate) : t("sales.errors.closedDate") }
      : message.values;
    return t(message.key, values ?? {});
  };
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { company_id } = useParams<{ company_id: string }>();
  const [me, setMe] = useState<AuthMe | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const isPro = !!me?.is_user_pro;

  const [date, setDate] = useState(ymdTodayLocal());
  // Due-date policy: for an on-account sale the field is PREFILLED from the
  // sale date ("due on receipt") and follows the sale date until the user
  // touches it (dueDateDirty). The user may pick another date or deliberately
  // clear it ("no due date"); a cleared field is sent as an explicit null.
  const [dueDate, setDueDate] = useState("");
  const [dueDateDirty, setDueDateDirty] = useState(false);
  // A due date may equal the sale date (the default) and must be allowed to be
  // in the past when the sale itself is back-dated -- so the floor is the sale
  // date, not today. This keeps the "due on receipt" default intact for
  // back-dated on-account sales (which are then correctly overdue).
  const minDueYmd = useMemo(() => date || ymdTodayLocal(), [date]);
  // Wrap the picker's onChange so any manual edit/clear marks the field dirty
  // and stops it from tracking further sale-date changes.
  const onDueDateChange = (ymd: string) => {
    setDueDateDirty(true);
    setDueDate(ymd);
  };

  // --- UI state ---
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [lines, setLines] = useState<Line[]>([{ ...defaultLine }]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [customerEmail, setCustomerEmail] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [error, setError] = useState<SalesMessage | null>("");
  const [saving, setSaving] = useState(false);
  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );
  const isClosedDate = useMemo(() => {
    return isDateClosed(date, closedThroughDate);
  }, [date, closedThroughDate]);
  const [customerComboKey, setCustomerComboKey] = useState(0);

  // credit terms
  const [isOnAccount, setIsOnAccount] = useState(false);

  // memo
  const [memo, setMemo] = useState("");

  // invoice send now (channel decided by backend/settings)
  const [sendEmailNow, setSendEmailNow] = useState(true);

  // --- Industry (for dropdown ordering only) ---
  const [industry, setIndustry] = useState<IndustryKey>("other");

  // --- Payment state ---
  const [amountPaid, setAmountPaid] = useState("");
  const [fee, setFee] = useState("");
  const [netAmount, setNetAmount] = useState("");
  const [overpayError, setOverpayError] = useState<SalesMessage | null>("");

  // Inlays
  const [showCustomerInlay, setShowCustomerInlay] = useState(false);
  const [productInlayIndex, setProductInlayIndex] = useState<number | null>(
    null,
  );

  // Stripe collection choice (Pay now vs Send link)
  const [stripeCollectMode, setStripeCollectMode] = useState<"now" | "link">(
    "now",
  );

  const [showStripeQrModal, setShowStripeQrModal] = useState(false);
  const [stripeCheckoutUrl, setStripeCheckoutUrl] = useState("");
  const checkoutSaleIdRef = useRef<number | null>(null);
  const [copiedCheckoutUrl, setCopiedCheckoutUrl] = useState(false);

  // scanCode
  const [scanCode, setScanCode] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<SalesMessage | null>("");

  const scanInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanLoopRef = useRef<number | null>(null);
  const hasInitialDraftRef = useRef(false);
  const lastPaymentMethodAppliedRef = useRef(false);
  const amountPaidTouchedRef = useRef(false);
  // FEE-RATE-SEMANTICS-1 Phase 2B: posting fee-intent provenance (configured vs
  // explicit) with draft-hydration protection. Held in a ref because the
  // auto-calc effect, the Fee input, the amount/method handlers, and submit all
  // read/update it. Default: configured.
  const feeIntentStateRef = useRef(initFeeIntentFromDraft(null));

  // POST /sales idempotency key: one key per logical Sale draft. Stable
  // across rerenders, retries, and ambiguous failures; only cleared on
  // confirmed success or explicit discard (see resetSaleEntry and
  // getOrCreateSaleIdempotencyKey below).
  const saleIdempotencyKeyRef = useRef<string | null>(null);
  // Fingerprint of the exact business payload sent the FIRST time the
  // current idempotency key was used. Bound once, at the first real
  // POST /sales attempt for this draft, alongside the key -- never
  // rebound on a later retry. A later retry whose payload fingerprints
  // differently is a materially edited retry and must be blocked rather
  // than silently sent (see submitSale's compare-or-bind logic).
  const submittedPayloadFingerprintRef = useRef<string | null>(null);
  // Synchronous guard against duplicate submitSale entry (React's `saving`
  // state alone only disables the button after a render commits, which is
  // not guaranteed to happen before a second, near-simultaneous submit
  // event is handled).
  const submitInFlightRef = useRef(false);

  // Fallback scanner (@zxing/browser) controls
  const zxingReaderRef = useRef<any>(null);
  const zxingControlsRef = useRef<{ stop?: () => void } | null>(null);

  const openExternalUrl = async (url: string) => {
    window.location.assign(url);
  };

  const resetSaleEntry = () => {
    const today = ymdTodayLocal();

    setLines([{ ...defaultLine }]);
    setSelectedCustomer(null);
    setCustomerEmail("");
    setDate(today);
    setDueDate(today);
    setPaymentMethodId("");
    setIsOnAccount(false);
    setMemo("");
    setAmountPaid("");
    setFee("");
    setNetAmount("");
    amountPaidTouchedRef.current = false;
    feeIntentStateRef.current = initFeeIntentFromDraft(null);
    setError("");
    setSendEmailNow(true);

    // A fresh blank draft is a genuinely new logical Sale -- called both
    // after confirmed success (finishAndExit and the Stripe-paylink
    // success branch) and on explicit discard (Back/Close/Cancel), which
    // are exactly the two cases these are allowed to rotate on. Every
    // caller of resetSaleEntry() also calls clearDraft(SALE_DRAFT_KEY),
    // which removes the persisted copy (see persistSaleIdempotencyState).
    // Always clear both together -- never one without the other.
    saleIdempotencyKeyRef.current = null;
    submittedPayloadFingerprintRef.current = null;
  };

  // Ref-only: returns the existing key, or mints and stores a new one if
  // this draft doesn't have one yet. Deliberately has no persistence
  // side effect -- see persistSaleIdempotencyState, which is the only
  // place the key is written to the draft, always together with its
  // matching fingerprint so the two can never be persisted out of sync.
  const getOrCreateSaleIdempotencyKey = (): string =>
    getOrCreateIdempotencyKey(saleIdempotencyKeyRef, randomUuidKey);

  // Atomic persistence: writes the key and the fingerprint of the payload
  // first sent under it into the existing localStorage draft in ONE
  // read-modify-write, so a remount/draft-restore can never observe a key
  // without its matching fingerprint (or vice versa) from THIS code path.
  // Called only once, at the moment a submit attempt is confirmed to be
  // the first real attempt for this draft -- see submitSale.
  const persistSaleIdempotencyState = (key: string, fingerprint: string) => {
    const existingDraft = loadDraft(SALE_DRAFT_KEY) || {};
    saveDraft(SALE_DRAFT_KEY, {
      ...existingDraft,
      idempotencyKey: key,
      submittedPayloadFingerprint: fingerprint,
    });
  };

  // --- LOAD DRAFT ON MOUNT ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      params.has("session_id") ||
      params.has("sid") ||
      params.get("stripe") === "1"
    ) {
      return;
    }

    const draft = loadDraft(SALE_DRAFT_KEY);
    if (draft) {
      const draftPaymentMatchesCompany =
        String(draft.companyId ?? "") === String(company_id ?? "");
      hasInitialDraftRef.current =
        draftPaymentMatchesCompany && Boolean(draft.paymentMethodId);
      setLines(draft.lines || [{ ...defaultLine }]);
      setSelectedCustomer(draft.selectedCustomer || null);
      setCustomerEmail(draft.customerEmail || "");
      setDate(ymdTodayLocal());
      setPaymentMethodId(
        draftPaymentMatchesCompany ? draft.paymentMethodId || "" : "",
      );
      setIsOnAccount(
        draftPaymentMatchesCompany ? draft.isOnAccount || false : false,
      );
      // Drafts do not persist a due date; restore it as empty rather than
      // fabricating today -- due_date stays optional and never auto-filled.
      setDueDate("");
      setMemo(draft.memo || "");
      if (draftPaymentMatchesCompany) {
        setAmountPaid(
          draft.amountPaid
            ? toInputMoney(parseMoneyInput(draft.amountPaid))
            : "",
        );
        if (draft.amountPaid) amountPaidTouchedRef.current = true;
        setFee(draft.fee ? toInputMoney(parseMoneyInput(draft.fee)) : "");
        setNetAmount(
          draft.netAmount ? toInputMoney(parseMoneyInput(draft.netAmount)) : "",
        );
        // FEE-RATE-SEMANTICS-1 Phase 2B (Condition 1): restore fee-intent
        // provenance. A pre-2B draft has no feeMode -> adopts configured (null).
        // A restored EXPLICIT intent is locked so the async auto-calc effect
        // cannot clobber the manual fee (see reduceOnAutoCalc).
        feeIntentStateRef.current = initFeeIntentFromDraft(
          draft.feeMode === "explicit" || draft.feeMode === "configured"
            ? (draft.feeMode as FeeMode)
            : null,
        );
      }
      if (typeof draft.sendEmailNow === "boolean") {
        setSendEmailNow(draft.sendEmailNow);
      }
      // Restore the key and its matching fingerprint as an associated
      // pair. A fingerprint is only ever trusted alongside a restored key
      // -- a fingerprint with no key is invalid local state and is
      // discarded here rather than left to block the user with an
      // unusable identity. A key restored WITHOUT a fingerprint (a
      // legacy draft saved before this correction existed) keeps the key
      // and leaves the fingerprint unbound; the next genuine submit
      // attempt then binds the current validated payload to it exactly
      // like a true first attempt -- see submitSale.
      if (typeof draft.idempotencyKey === "string" && draft.idempotencyKey) {
        saleIdempotencyKeyRef.current = draft.idempotencyKey;
        if (
          typeof draft.submittedPayloadFingerprint === "string" &&
          draft.submittedPayloadFingerprint
        ) {
          submittedPayloadFingerprintRef.current =
            draft.submittedPayloadFingerprint;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Load current user (for Pro gating) ---
  useEffect(() => {
    apiGet<AuthMe>("/auth/me")
      .then(setMe)
      .catch(() => setMe(null));
  }, []);

  // --- SAVE DRAFT ON CHANGE (debounced) ---
  useEffect(() => {
    const handler = setTimeout(() => {
      if (checkoutSaleIdRef.current !== null) return;
      saveDraft(SALE_DRAFT_KEY, {
        companyId: company_id,
        lines,
        selectedCustomer,
        customerEmail,
        paymentMethodId,
        isOnAccount,
        memo,
        amountPaid,
        fee,
        netAmount,
        sendEmailNow,
        // FEE-RATE-SEMANTICS-1 Phase 2B: persist fee-intent provenance so a
        // restored draft keeps configured-vs-explicit intent (read live off the
        // ref; not a reactive dep).
        feeMode: feeIntentStateRef.current.mode,
        // Carry forward whatever key/fingerprint have already been bound
        // (read live off the refs -- they are not reactive state and
        // intentionally not dependencies below), together, so an
        // ordinary field edit after a submit attempt never drops one
        // while keeping the other.
        idempotencyKey: saleIdempotencyKeyRef.current,
        submittedPayloadFingerprint: submittedPayloadFingerprintRef.current,
      });
    }, 300);
    return () => clearTimeout(handler);
  }, [
    lines,
    selectedCustomer,
    customerEmail,
    paymentMethodId,
    isOnAccount,
    memo,
    amountPaid,
    fee,
    netAmount,
    sendEmailNow,
    company_id,
  ]);

  // --- Load reference data (scoped by company) ---
  useEffect(() => {
    if (!company_id) return;
    apiGet<Product[]>(`/products?company_id=${company_id}`)
      .then(setProducts)
      .catch(() => setProducts([]));
    apiGet<Customer[]>(`/customers?company_id=${company_id}`)
      .then(setCustomers)
      .catch(() => setCustomers([]));
    apiGet<PaymentMethod[]>(`/payment-methods?company_id=${company_id}`)
      .then(setPaymentMethods)
      .catch(() => setPaymentMethods([]));
    apiGet<Account[]>(`/accounts?company_id=${company_id}`)
      .then((xs) => setAccounts(Array.isArray(xs) ? xs : []))
      .catch(() => setAccounts([]));
  }, [company_id]);

  // Load company industry
  useEffect(() => {
    if (!company_id) return;
    apiGet<{ value?: string }>(`/settings/industry?company_id=${company_id}`)
      .then((res) => setIndustry(normalizeIndustry(res?.value)))
      .catch(() => setIndustry("other"));
  }, [company_id]);

  // Build lists and Stripe defaults
  const { displayMethods, hasStripe, stripeDefaultId } = useMemo(() => {
    const stripeMethods = paymentMethods.filter((pm) => isStripePM(pm));
    const stripeDefault = stripeMethods.length
      ? String(stripeMethods[0].id)
      : null;

    const validPaymentAccountIds = new Set(
      accounts
        .filter(isValidSalePaymentAccount)
        .map((account) => String(account.id)),
    );

    const nonStripe = paymentMethods
      .filter(
        (pm) =>
          !isStripePM(pm) && validPaymentAccountIds.has(String(pm.account_id)),
      )
      .slice()
      .sort(
        (a, b) => pmWeight(a) - pmWeight(b) || a.name.localeCompare(b.name),
      );

    return {
      displayMethods: nonStripe,
      hasStripe: stripeDefault !== null,
      stripeDefaultId: stripeDefault,
    };
  }, [paymentMethods, accounts]);

  const grandTotal = lines.reduce(
    (sum, line) => sum + (parseFloat(line.total) || 0),
    0,
  );

  useEffect(() => {
    if (!company_id) return;
    if (lastPaymentMethodAppliedRef.current) return;
    if (hasInitialDraftRef.current) return;
    if (paymentMethodId) return;

    const remembered = readLastPaymentMethod("sales", company_id);
    if (!remembered) {
      lastPaymentMethodAppliedRef.current = true;
      return;
    }

    if (remembered === STRIPE_SYNTH) {
      if (hasStripe) {
        setPaymentMethodId(STRIPE_SYNTH);
        setOverpayError("");
        amountPaidTouchedRef.current = false;
        lastPaymentMethodAppliedRef.current = true;
      }
      return;
    }

    const pm = displayMethods.find((pm) => String(pm.id) === remembered);
    if (pm) {
      setPaymentMethodId(remembered);
      setOverpayError("");
      if (isDeferredSettlement(pm?.type)) {
        setAmountPaid("");
        setFee("");
        setNetAmount("");
        amountPaidTouchedRef.current = false;
      } else {
        amountPaidTouchedRef.current = false;
      }
      lastPaymentMethodAppliedRef.current = true;
    }
  }, [company_id, displayMethods, hasStripe, paymentMethodId]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  // If a draft held a real Stripe id, map it to synthetic Stripe option
  useEffect(() => {
    if (!paymentMethodId) return;
    if (paymentMethodId === STRIPE_SYNTH) return;
    const pm = paymentMethods.find((m) => String(m.id) === paymentMethodId);
    if (pm && isStripePM(pm)) setPaymentMethodId(STRIPE_SYNTH);
  }, [paymentMethods, paymentMethodId]);

  const handleCustomerChange = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    setCustomerEmail(customer?.email || "");
  };

  const handleItemChange = (idx: number, product: Product | null) => {
    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== idx) return line;

        if (product === null) {
          const qty = line.qty || "1";
          const price = line.price || "";
          const discount = line.discount || "0";
          const total = price ? getLineTotal(qty, price, discount) : "0";

          return {
            ...line,
            product_id: "",
            item_kind: undefined,
            detail: line.detail || "",
            qty,
            price,
            discount,
            total,
          };
        }

        if (product.id === "") {
          const qty = line.qty || "1";
          const price = line.price || "";
          const discount = line.discount || "0";
          const total = price ? getLineTotal(qty, price, discount) : "0";

          return {
            ...line,
            product_id: "",
            item_kind: "service",
            detail: line.detail || "",
            qty,
            price,
            discount,
            total,
          };
        }

        const sellPrice = product?.sell_price?.toString() ?? "0";
        return {
          ...line,
          product_id: String(product.id),
          item_kind: product?.is_service ? "service" : "product",
          detail: "",
          qty: "1",
          price: sellPrice,
          discount: "0",
          total: getLineTotal("1", sellPrice, "0"),
        };
      }),
    );
  };

  const applyScannedProduct = (product: Product) => {
    const sellPrice = product?.sell_price?.toString() ?? "0";

    setLines((prev) => {
      const existingIndex = prev.findIndex(
        (line) => String(line.product_id) === String(product.id),
      );

      if (existingIndex >= 0) {
        return prev.map((line, idx) => {
          if (idx !== existingIndex) return line;

          const nextQty = String((parseFloat(line.qty || "0") || 0) + 1);

          return {
            ...line,
            qty: nextQty,
            total: getLineTotal(
              nextQty,
              line.price || sellPrice,
              line.discount || "0",
            ),
          };
        });
      }

      const newLine: Line = {
        ...defaultLine,
        product_id: String(product.id),
        item_kind: product.is_service ? "service" : "product",
        detail: "",
        qty: "1",
        price: sellPrice,
        discount: "0",
        total: getLineTotal("1", sellPrice, "0"),
      };

      const hasOnlyBlankLine =
        prev.length === 1 &&
        !prev[0].product_id &&
        !prev[0].detail &&
        parseMoneyInput(prev[0].total) === 0;

      return hasOnlyBlankLine ? [newLine] : [...prev, newLine];
    });
  };

  const handleScanSubmit = (rawCode: string) => {
    const code = rawCode.trim();
    if (!code) return;

    const product = products.find((p) =>
      [p.barcode, p.sku, p.product_code, String(p.id)]
        .filter(Boolean)
        .some((v) => String(v).trim() === code),
    );

    if (!product) {
      setError(localMessage("sales.errors.barcodeNotFound", { code }));
      setScanCode("");
      scanInputRef.current?.focus();
      return;
    }

    setError("");
    applyScannedProduct(product);
    setScanCode("");
    scanInputRef.current?.focus();
  };

  const stopCameraScan = useCallback(() => {
    if (scanLoopRef.current) {
      cancelAnimationFrame(scanLoopRef.current);
      scanLoopRef.current = null;
    }

    try {
      zxingControlsRef.current?.stop?.();
    } catch {}
    zxingControlsRef.current = null;

    try {
      zxingReaderRef.current?.reset?.();
    } catch {}
    zxingReaderRef.current = null;

    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch {}
      (videoRef.current as HTMLVideoElement).srcObject = null;
    }

    setCameraOpen(false);
  }, []);

  const startCameraScan = async () => {
    setCameraError("");

    try {
      setCameraOpen(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      // 1) Native path (works in supporting browsers)
      if ("BarcodeDetector" in window) {
        const detector = new (window as any).BarcodeDetector({
          formats: ["qr_code", "ean_13", "ean_8", "code_128", "upc_a", "upc_e"],
        });

        const scan = async () => {
          if (!videoRef.current) return;

          try {
            const codes = await detector.detect(videoRef.current);
            const value = codes?.[0]?.rawValue;

            if (value) {
              stopCameraScan();
              handleScanSubmit(value);
              return;
            }
          } catch {}

          scanLoopRef.current = requestAnimationFrame(scan);
        };

        scan();
        return;
      }

      // 2) iPhone Safari fallback path
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      const reader = new BrowserMultiFormatReader();
      zxingReaderRef.current = reader;

      const controls = await reader.decodeFromVideoElement(
        videoRef.current as HTMLVideoElement,
        (result: any, err: any) => {
          if (result) {
            const text =
              typeof result.getText === "function" ? result.getText() : "";
            if (text) {
              stopCameraScan();
              handleScanSubmit(text);
            }
          }
        },
      );

      zxingControlsRef.current = controls;
    } catch (err: any) {
      setCameraError(
        localMessage("sales.errors.cameraUnavailable"),
      );
      stopCameraScan();
    }
  };

  const sanitizeNumberInput = (v: string) => {
    let s = v.replace(/[^0-9.]/g, "");
    const parts = s.split(".");
    if (parts.length > 2) s = parts[0] + "." + parts.slice(1).join("");
    return s;
  };

  const handleLineChange = (
    idx: number,
    field: keyof Line,
    rawValue: string,
  ) => {
    setLines((lines) =>
      lines.map((line, i) => {
        if (i !== idx) return line;

        if (field === "detail") {
          return { ...line, detail: rawValue };
        }

        const value = sanitizeNumberInput(rawValue);
        let qty = line.qty;
        let price = line.price;
        let discount = line.discount;
        let total = line.total;
        const lastEdited: "price" | "discount" | "total" | undefined =
          field as any;

        if (field === "qty") {
          qty =
            value === "" || value === "0"
              ? "1"
              : value.replace(/^0+/, "") || "1";
        } else if (field === "price") {
          price = value;
        } else if (field === "discount") {
          // SALE-DISCOUNT-TYPING-1: preserve the raw sanitized string while
          // typing so an in-progress decimal ("3.", ".5") is never destroyed
          // by a parseFloat(value).toString() round-trip (which turned an
          // intended 3.5 into 35). Only the existing upper-bound clamp is kept,
          // and it never re-stringifies a valid live value.
          discount = value;
          const num = parseFloat(value);
          if (Number.isFinite(num) && num > 100) {
            discount = "100";
          }
        } else if (field === "total") {
          total = value;
        }

        if (field === "total") {
          const nQty = parseFloat(qty) || 1;
          const nPrice = parseFloat(price) || 0;
          const nTotal = parseFloat(total) || 0;
          if (nQty && nPrice) {
            let origTotal = nQty * nPrice;
            let newDiscount =
              origTotal === 0 ? 0 : (1 - nTotal / origTotal) * 100;
            if (newDiscount < 0) {
              newDiscount = 0;
              price =
                nQty === 0
                  ? "0"
                  : String(Math.round((nTotal / nQty) * 100) / 100);
            }
            discount =
              Math.round(Math.max(0, Math.min(100, newDiscount)) * 100) / 100 +
              "";
          } else if (nQty) {
            price =
              nQty === 0
                ? "0"
                : String(Math.round((nTotal / nQty) * 100) / 100);
            discount = "0";
          }
        } else if (field === "discount") {
          if (
            discount === "0" &&
            parseFloat(total) !== parseFloat(qty) * parseFloat(price)
          ) {
            price = String(
              Math.round((parseFloat(total) / (parseFloat(qty) || 1)) * 100) /
                100,
            );
          }
          total = getLineTotal(qty, price, discount);
        } else {
          total = getLineTotal(qty, price, discount);
        }

        return {
          ...line,
          qty,
          price,
          discount,
          total,
          lastEdited,
        };
      }),
    );
  };

  const handleBlur = (idx: number, field: keyof Line, value: string) => {
    setLines((lines) =>
      lines.map((line, i) => {
        if (i !== idx) return line;
        let v = value;
        if (v.includes(".")) {
          const [int, dec] = v.split(".");
          v = int + (dec ? "." + dec.slice(0, 2) : "");
        }
        if (v.endsWith(".")) v = v.slice(0, -1);
        return { ...line, [field]: v };
      }),
    );
  };

  const addLine = () => setLines([...lines, { ...defaultLine }]);
  const removeLine = (idx: number) =>
    setLines((lines) => lines.filter((_, i) => i !== idx));

  const handlePaymentMethodSelect = (value: string) => {
    setPaymentMethodId(value);
    setOverpayError("");
    // FEE-RATE-SEMANTICS-1 Phase 2B: a genuine user method change resets to
    // configured and releases any restored explicit lock, so the auto-calc
    // effect recalculates the configured preview.
    feeIntentStateRef.current = reduceOnUserAmountOrMethodChange(
      feeIntentStateRef.current,
    );

    if (!value) {
      setAmountPaid("");
      setFee("");
      setNetAmount("");
      amountPaidTouchedRef.current = false;
      return;
    }

    if (value !== STRIPE_SYNTH) {
      const pm = paymentMethods.find((pm) => String(pm.id) === value) || null;
      if (isDeferredSettlement(pm?.type)) {
        setAmountPaid("");
        setFee("");
        setNetAmount("");
        amountPaidTouchedRef.current = false;
        return;
      }
    }

    amountPaidTouchedRef.current = false;
    if (grandTotal > 0) {
      setAmountPaid(toInputMoney(grandTotal));
    } else {
      setAmountPaid("");
    }
  };

  useEffect(() => {
    if (!paymentMethodId || amountPaidTouchedRef.current) return;

    if (paymentMethodId !== STRIPE_SYNTH) {
      const pm =
        paymentMethods.find((pm) => String(pm.id) === paymentMethodId) || null;
      if (!pm || isDeferredSettlement(pm?.type)) return;
    }

    const target = grandTotal > 0 ? toInputMoney(grandTotal) : "";
    if (amountPaid !== target) setAmountPaid(target);
  }, [amountPaid, grandTotal, paymentMethodId, paymentMethods]);

  useEffect(() => {
    return () => stopCameraScan();
  }, [stopCameraScan]);

  // Track On Account from selected method; Stripe synthetic is never On Account
  useEffect(() => {
    if (paymentMethodId === STRIPE_SYNTH) {
      setIsOnAccount(false);
      return;
    }
    const pm =
      paymentMethods.find((pm) => String(pm.id) === paymentMethodId) || null;
    const onAcct = isDeferredSettlement(pm?.type);
    setIsOnAccount(onAcct);
    // Leaving on-account resets the field to its pristine (undirtied) state so
    // that returning to on-account re-prefills the default from the sale date.
    if (!onAcct) {
      setDueDate("");
      setDueDateDirty(false);
    }
  }, [paymentMethodId, paymentMethods]);

  // Prefill / follow: while the user has not touched the field, an on-account
  // due date tracks the sale date (the visible "due on receipt" default).
  useEffect(() => {
    if (isOnAccount && !dueDateDirty) setDueDate(date);
  }, [isOnAccount, date, dueDateDirty]);

  // Clamp an ENTERED due date to be no earlier than the sale date. A cleared
  // field ("") is left cleared -- it is a deliberate "no due date" choice.
  useEffect(() => {
    if (!isOnAccount) return;
    if (dueDate && dueDate < minDueYmd) setDueDate(minDueYmd);
  }, [isOnAccount, dueDate, minDueYmd]);

  const stripeSelected = paymentMethodId === STRIPE_SYNTH;
  const selectedPaymentMethod = stripeSelected
    ? null
    : paymentMethods.find((pm) => String(pm.id) === paymentMethodId) || null;
  const cashSelected = isPMCash(selectedPaymentMethod);

  // --- Fee/Net calc: skip when Stripe synthetic is selected
  useEffect(() => {
    const amt = parseMoneyInput(amountPaid);

    if (stripeSelected) {
      setFee("");
      setNetAmount(amountPaid ? toInputMoney(amt) : "");
      return;
    }

    // FEE-RATE-SEMANTICS-1 Phase 2B (Condition 1): this is an auto-calc firing.
    // A restored/locked EXPLICIT intent must survive it (and every later async
    // firing) -- reduceOnAutoCalc keeps the lock, so a manual fee restored from
    // a draft is never clobbered when paymentMethods arrives later.
    const nextIntent = reduceOnAutoCalc(feeIntentStateRef.current);
    feeIntentStateRef.current = nextIntent;

    const pm = paymentMethods.find((pm) => String(pm.id) === paymentMethodId);

    if (nextIntent.mode === "explicit") {
      // Preserve the user's manual fee; only refresh the derived Net.
      const f = parseMoneyInput(fee);
      setNetAmount(amountPaid ? toInputMoney(amt - f) : "");
      return;
    }

    if (pm && amt > 0) {
      // Non-authoritative preview from the derived percent (fraction = %/100,
      // exactly once). Cash ignores configured rate AND fixed fee.
      const rate = isPMCash(pm) ? 0 : paymentMethodFeeFraction(pm);
      const fixed = isPMCash(pm) ? 0 : paymentMethodFixedFee(pm);
      const calcFee = Math.round((amt * rate + fixed) * 100) / 100;

      setFee(toInputMoney(calcFee));
      setNetAmount(toInputMoney(amt - calcFee));
    } else {
      setFee("");
      setNetAmount(amountPaid ? toInputMoney(amt) : "");
    }
    // `fee` is intentionally read (not a dep) to refresh Net for a locked
    // explicit intent without re-triggering this effect on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodId, amountPaid, paymentMethods, stripeSelected]);
  // prevent overpayment
  useEffect(() => {
    const paid = parseMoneyInput(amountPaid);
    const max = grandTotal;
    if (!isOnAccount && paymentMethodId && paid > max) {
      setOverpayError(
        localMessage("sales.errors.paidAboveOutstanding", { paid: paid.toFixed(2), outstanding: max.toFixed(2) }),
      );
    } else {
      setOverpayError("");
    }
  }, [amountPaid, grandTotal, paymentMethodId, isOnAccount]);

  const preorderStatus = getPreorderStatus(lines, products);

  // Single toggle only. Pro user decides channel order in Settings.
  const canSendInvoiceNow = isPro && Boolean(selectedCustomer?.id);
  const customerMobile =
    selectedCustomer?.mobile || selectedCustomer?.phone || "";
  const customerEmailDisplay = selectedCustomer?.email || "";
  const hasCustomerContact = Boolean(customerMobile || customerEmailDisplay);

  // ---------- helpers ----------
  const finishAndExit = async () => {
    clearDraft(SALE_DRAFT_KEY);
    resetSaleEntry();
    navigate(companyRoute(company_id!, "/sales"), { replace: true });
  };

  const resolveInvoiceId = (saleRes: any): number | null => {
    return (
      saleRes?.invoice_id || saleRes?.invoice?.id || saleRes?.invoiceId || null
    );
  };

  const triggerInvoiceSendoff = async (
    saleRes: any,
    opts?: {
      forceEmailSend?: boolean;
    },
  ): Promise<"whatsapp" | "email" | "none"> => {
    if (!(sendEmailNow && canSendInvoiceNow)) {
      return "none";
    }

    try {
      const invoiceId = resolveInvoiceId(saleRes);
      if (!invoiceId || !company_id) {
        return "none";
      }

      posthog.capture("invoice_auto_send_triggered", {
        sale_id: saleRes?.id,
        invoice_id: invoiceId,
        company_id,
        source: "sale_entry",
        trigger_type: "auto",
        user_plan: isPro ? "pro" : "basic",
        has_customer_email: !!selectedCustomer?.email,
        has_customer_mobile: !!(
          selectedCustomer?.mobile || selectedCustomer?.phone
        ),
      });

      const sendoff = await apiGet<InvoiceSendoff>(
        `/invoices/${invoiceId}/sendoff?company_id=${company_id}`,
      );

      const launchChannel =
        sendoff?.launch_channel || (sendoff?.launch_url ? "whatsapp" : null);

      const launchUrl = sendoff?.launch_url || null;

      if (launchChannel === "whatsapp" && launchUrl) {
        await openExternalUrl(launchUrl);

        posthog.capture("invoice_auto_send_dispatched", {
          sale_id: saleRes?.id,
          invoice_id: invoiceId,
          company_id,
          source: "sale_entry",
          trigger_type: "auto",
          user_plan: isPro ? "pro" : "basic",
          channel: "whatsapp",
        });

        return "whatsapp";
      }

      if (launchChannel === "email") {
        await apiPost(
          `/invoices/${invoiceId}/send-email-now?company_id=${company_id}`,
          {},
        );

        posthog.capture("invoice_auto_send_dispatched", {
          sale_id: saleRes?.id,
          invoice_id: invoiceId,
          company_id,
          source: "sale_entry",
          trigger_type: "auto",
          user_plan: isPro ? "pro" : "basic",
          channel: "email",
        });

        return "email";
      }

      if (
        sendoff?.customer_has_email === false &&
        sendoff?.customer_has_mobile === false
      ) {
        setError(
          localMessage("sales.errors.sendMissingContact"),
        );
      }

      posthog.capture("invoice_auto_send_dispatched", {
        sale_id: saleRes?.id,
        invoice_id: invoiceId,
        company_id,
        source: "sale_entry",
        trigger_type: "auto",
        user_plan: isPro ? "pro" : "basic",
        channel: "none",
      });

      return "none";
    } catch (err) {
      console.error("Failed to prepare invoice sendoff:", err);

      posthog.capture("invoice_auto_send_dispatched", {
        sale_id: saleRes?.id,
        invoice_id: resolveInvoiceId(saleRes),
        company_id,
        source: "sale_entry",
        trigger_type: "auto",
        user_plan: isPro ? "pro" : "basic",
        channel: "error",
      });

      return "none";
    }
  };

  const openCustomerInlay = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setCustomerComboKey((k) => k + 1);
    setShowCustomerInlay(true);
  };

  const handleCopyCheckoutLink = async () => {
    if (!stripeCheckoutUrl) return;
    try {
      await navigator.clipboard.writeText(stripeCheckoutUrl);
      setCopiedCheckoutUrl(true);
      window.setTimeout(() => setCopiedCheckoutUrl(false), 2000);
    } catch {}
  };

  // --- Finalize after returning from Stripe (idempotent, resilient) ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isStripeReturn =
      params.has("session_id") ||
      params.has("sid") ||
      params.get("stripe") === "1";
    if (!isStripeReturn) return;

    const onceKey = `rb_finalized_${
      params.get("session_id") ||
      params.get("sid") ||
      params.get("invoice_id") ||
      "x"
    }`;
    if (sessionStorage.getItem(onceKey)) return;
    sessionStorage.setItem(onceKey, "1");

    (async () => {
      clearDraft(SALE_DRAFT_KEY);
      const sid = params.get("session_id") || params.get("sid") || "";
      let invoiceId: number | null =
        Number(params.get("invoice_id") || "0") || null;
      const saleId =
        Number(params.get("sale_id") || params.get("s") || "0") || undefined;

      if (sid && saleId) {
        try {
          await apiPost(
            `/stripe/sales/${saleId}/gateway/finalize-from-session?company_id=${company_id}`,
            { cs: sid },
          );
        } catch {}
      }

      if (!invoiceId && sid) {
        try {
          const r = await apiGet<{ invoice_id?: number }>(
            `/payments/session/${sid}/resolve`,
          );
          invoiceId = r?.invoice_id ?? null;
        } catch {}
      }

      if (invoiceId) {
        try {
          for (let i = 0; i < 3; i++) {
            const res = await apiPost(
              `/payments/invoices/${invoiceId}/finalize`,
              {},
            );
            if (res?.ready || res?.ok) break;
            await new Promise((r) => setTimeout(r, 800));
          }
        } catch {}
      }

      await finishAndExit();

      const url = new URL(window.location.href);
      ["session_id", "sid", "stripe", "sale_id", "s", "invoice_id"].forEach(
        (k) => url.searchParams.delete(k),
      );
      window.history.replaceState({}, "", url.toString());
    })();
  }, [company_id]);

  // ------------------------------
  // Unified submit function
  // ------------------------------
  const submitSale = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (checkoutSaleIdRef.current !== null) return;
    setError("");

    if (!paymentMethodId) {
      setError(localMessage("sales.errors.selectPaymentMethod"));
      return;
    }

    const wantsStripeOnline = paymentMethodId === STRIPE_SYNTH;
    const deferInvoiceSendUntilPayLink =
      wantsStripeOnline &&
      stripeCollectMode === "link" &&
      sendEmailNow &&
      canSendInvoiceNow;

    const pm = wantsStripeOnline
      ? null
      : paymentMethods.find((m) => String(m.id) === paymentMethodId) || null;
    const stripePMId = wantsStripeOnline
      ? Number(stripeDefaultId) || null
      : null;

    // FEE-RATE-SEMANTICS-1 Phase 2B (Conditions 4a/4b, Finding 3): resolve the
    // posting fee fields via the SHARED production builder (also unit-tested).
    // Configured -> no fee (backend authoritative); explicit -> strictly
    // validated numeric fee; cash -> explicit 0; no positive payment -> neither.
    const feeResolution = resolveSaleFeeFields({
      isOnAccount,
      wantsStripeOnline,
      amountPaid: amountPaid ? parseMoneyInput(amountPaid) : 0,
      isCash: isPMCash(pm),
      feeMode: feeIntentStateRef.current.mode,
      feeRaw: fee,
    });
    if (!feeResolution.ok) {
      setError(
        localMessage("sales.errors.invalidEntryFee"),
      );
      return;
    }
    const hasPositiveOfflinePayment = feeResolution.hasPositiveOfflinePayment;
    const feeFields = feeResolution.feeFields;

    if (wantsStripeOnline && !stripePMId) {
      setError(localMessage("sales.errors.stripeNotConfigured"));
      return;
    }

    if (!isOnAccount) {
      const paid = parseMoneyInput(amountPaid);
      const max = grandTotal;
      if (paid < 0) {
        setError(localMessage("sales.errors.negativePayment"));
        return;
      }
      if (paid > max) {
        setError(
          localMessage("sales.errors.entryOverpayment", { amount: formatMoney(grandTotal) }),
        );
        return;
      }
    }

    const badLines = lines.some((l) => {
      const isService = !l.product_id;
      const unitPrice = parseFloat(l.price || "0");
      const hasDetail = !!(l.detail && l.detail.trim());
      if (isService && !hasDetail) return true;
      if (unitPrice < 0) return true;
      if (!(parseFloat(l.qty || "0") > 0)) return true;
      return false;
    });
    if (badLines) {
      setError(
        localMessage("sales.errors.invalidLine"),
      );
      return;
    }

    if (isOnAccount && dueDate) {
      // Only validate when a due date is actually present -- an on-account sale
      // may be recorded with no due date at all (deliberate clear). The floor
      // is the sale date so the "due on receipt" default (and back-dated,
      // already-overdue receivables) is accepted.
      if (dueDate < date) {
        setError(localMessage("sales.errors.entryDueDate", { date }));
        return;
      }
    }

    // Synchronous guard: everything above this point is synchronous, so no
    // second submitSale invocation (rapid double-click/tap) can interleave
    // before this check -- see submitInFlightRef's declaration. Placed
    // immediately before the first `await` in this function.
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    setSaving(true);

    try {
      // The exact business payload for POST /sales, EXCLUDING
      // idempotency_key -- this is the single source of truth for both
      // the fingerprint below and (with idempotency_key added) the
      // actual request body, so the two can never drift apart.
      const payloadWithoutIdempotencyKey: SalePostBody = {
        date,
        lines: lines.map((line, idx): SaleLinePostBody => {
          const qtyNum = Number(line.qty) || 0;
          const priceNum = Number(line.price) || 0;
          const totalNum = Number(line.total) || 0;
          const base = qtyNum * priceNum;

          const derivedDiscount = base > 0 ? (1 - totalNum / base) * 100 : 0;

          const discountToSend =
            line.lastEdited === "total"
              ? Number(Math.max(0, Math.min(100, derivedDiscount)).toFixed(6))
              : Number(line.discount || 0);

          const selectedProduct = products.find(
            (p) => String(p.id) === line.product_id,
          );
          const isServiceProduct = !!selectedProduct?.is_service;

          return {
            product_id: line.product_id ? Number(line.product_id) : null,
            detail: !line.product_id ? line.detail : undefined,
            qty: qtyNum,
            unit_price: priceNum,
            discount: discountToSend,
            item_kind:
              isServiceProduct || !line.product_id ? "service" : "product",
            preorder: isServiceProduct ? false : preorderStatus[idx],
          };
        }),
        payment_method_id: wantsStripeOnline
          ? Number(stripePMId)
          : Number(paymentMethodId),
        on_account: isOnAccount,
        memo,
        send_email_now: !!(
          sendEmailNow &&
          canSendInvoiceNow &&
          !deferInvoiceSendUntilPayLink
        ),
        // Discriminated fee-intent: configured (no fee), explicit (numeric fee),
        // or neither (no positive offline payment). See buildFeeIntent above.
        ...feeFields,
      };

      if (selectedCustomer?.id) {
        payloadWithoutIdempotencyKey.customer_id = Number(selectedCustomer.id);
      }

      // Due-date policy: for an on-account sale always send the field so the
      // server stores exactly what the user sees -- the prefilled/edited date,
      // or an explicit null when the user deliberately cleared it ("no due
      // date"). Non-account sales never carry a due date.
      if (isOnAccount)
        payloadWithoutIdempotencyKey.due_date = dueDate ? dueDate : null;

      if (hasPositiveOfflinePayment) {
        // Fee is carried by ...feeFields (fee_mode discriminated); never a bare
        // fee here.
        payloadWithoutIdempotencyKey.amount_paid = parseMoneyInput(amountPaid);
        if (pm && pm.account_id) {
          payloadWithoutIdempotencyKey.payment_account_id = pm.account_id;
        }
      }

      // Bind (first attempt) or verify (retry) this attempt's identity
      // BEFORE sending anything -- see submittedPayloadFingerprintRef's
      // declaration for why an edited retry must never reach apiPost.
      const currentFingerprint = fingerprintSalePayload(
        payloadWithoutIdempotencyKey,
      );
      const key = getOrCreateSaleIdempotencyKey();
      const decision = decideSaleSubmissionIdentity(
        submittedPayloadFingerprintRef.current,
        currentFingerprint,
      );

      if (decision === "mismatch") {
        // Edited retry: sending this now would either create a second,
        // different Sale under a fresh identity (if the first attempt
        // never reached the server) or -- worse -- silently discard
        // these edits if the backend replays the ORIGINAL Sale (if the
        // first attempt actually succeeded but the response was lost).
        // Block instead of guessing; the key and fingerprint are left
        // exactly as they were.
        //
        // FEE-RATE-SEMANTICS-1 Phase 2B (Condition 3): a pre-2B (old-shape)
        // bound fingerprint can never match a current-shape retry -- even with
        // identical details -- so it gets distinct guidance that does NOT imply
        // an unchanged retry resolves it. The key/fingerprint are never rebound
        // or cleared here either way.
        setError(
          isLegacySaleFingerprint(submittedPayloadFingerprintRef.current)
            ? localMessage("sales.retry.saleLegacy")
            : localMessage("sales.retry.saleEdited"),
        );
        return;
      }

      if (decision === "first-attempt") {
        submittedPayloadFingerprintRef.current = currentFingerprint;
        persistSaleIdempotencyState(key, currentFingerprint);
      }
      // decision === "unchanged-retry": same key, same fingerprint,
      // nothing to (re)persist -- proceed exactly as a first attempt
      // would, below.

      // FEE-RATE-SEMANTICS-1 Phase 2B (Finding 1): the ACTUAL posted object is
      // typed (SalePostRequest = SalePostBody & { idempotency_key }), never any.
      const payload: SalePostRequest = {
        ...payloadWithoutIdempotencyKey,
        idempotency_key: key,
      };
      const saleRes = await apiPost(`/sales?company_id=${company_id}`, payload);
      if (!saleRes || !saleRes.id) throw new LocalSalesError(localMessage("sales.errors.saleNotCreated"));
      writeLastPaymentMethod("sales", company_id, paymentMethodId);

      const shouldRunImmediateSendoff =
        !deferInvoiceSendUntilPayLink &&
        !(wantsStripeOnline && stripeCollectMode === "now");

      if (shouldRunImmediateSendoff) {
        const sendoffChannel = await triggerInvoiceSendoff(saleRes);
        if (sendoffChannel === "whatsapp") {
          clearDraft(SALE_DRAFT_KEY);
          resetSaleEntry();
          return;
        }
      }

      if (wantsStripeOnline) {
        const amt = parseMoneyInput(amountPaid);
        const usePartial =
          amt > 0 && amt <= grandTotal ? Math.round(amt * 100) : undefined;

        if (stripeCollectMode === "now") {
          const body = usePartial ? { amount_cents: usePartial } : {};
          const r = await apiPost(
            `/stripe/sales/${saleRes.id}/checkout?company_id=${company_id}&return_to=entry`,
            body,
          );
          if (!r?.url) {
            setError(localMessage("sales.errors.stripeCheckout"));
            setSaving(false);
            return;
          }

          checkoutSaleIdRef.current = Number(saleRes.id);
          clearDraft(SALE_DRAFT_KEY);
          setStripeCheckoutUrl(String(r.url));
          setCopiedCheckoutUrl(false);
          setSaving(false);
          setShowStripeQrModal(true);
          return;
        } else {
          const linkRes = await apiPost(
            `/stripe/sales/${saleRes.id}/paylink?company_id=${company_id}`,
            usePartial ? { amount_cents: usePartial } : {},
          );
          const evergreenUrl = linkRes?.url as string | undefined;
          if (!evergreenUrl) {
            setError(localMessage("sales.errors.stripePayLink"));
            setSaving(false);
            return;
          }

          const sendoffChannel = await triggerInvoiceSendoff(saleRes, {
            forceEmailSend: true,
          });

          clearDraft(SALE_DRAFT_KEY);
          if (sendoffChannel === "whatsapp") {
            resetSaleEntry();
            return;
          }

          if (!isPro) {
            resetSaleEntry();
            navigate(companyRoute(company_id!, `/sales/${saleRes.id}`), {
              replace: true,
            });
            return;
          }

          await finishAndExit();
          return;
        }
      }

      await finishAndExit();
      return;
    } catch (err: any) {
      if (isClosedPeriodError(err)) {
        setError(localClosedPeriod(closedThroughDate));
        return;
      }

      if (err?.response?.status === 409 && err?.response?.data?.detail) {
        setError(localApiDetail(err));
        return;
      }

      setError(asUserMessage(err));
      // Deliberately no key or fingerprint rotation here: an unchanged
      // retry must reuse both, and an edited retry must be BLOCKED (see
      // the mismatch branch above), never silently rotated -- see
      // getOrCreateSaleIdempotencyKey / resetSaleEntry.
    } finally {
      setSaving(false);
      submitInFlightRef.current = false;
    }
  };

  const itemOptions = useMemo(() => {
    const serviceOption: Product = { id: "", name: "Service" };
    const hasProducts = products.length > 0;
    const serviceFirst =
      industry === "services" ||
      industry === "professional" ||
      industry === "online_digital";

    if (!hasProducts) return [serviceOption, ...products];

    return serviceFirst
      ? [serviceOption, ...products]
      : [...products, serviceOption];
  }, [products, industry]);

  const getSelectedItemOption = (line: Line) => {
    const selectedProduct = products.find(
      (product) => String(product.id) === line.product_id,
    );
    if (selectedProduct) return selectedProduct;

    if (line.product_id === "" && line.item_kind === "service") {
      return itemOptions.find((option) => option.id === "") || null;
    }

    return null;
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center justify-start px-3 py-8 ${A.body}`}
    >
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => {
            clearDraft(SALE_DRAFT_KEY);
            resetSaleEntry();
            navigate(-1);
          }}
          className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`${A.title} text-center flex-1 tracking-tight`}>
          {t("sales.common.newSale")}
        </h2>

        <button
          type="button"
          onClick={() => {
            clearDraft(SALE_DRAFT_KEY);
            resetSaleEntry();
            navigate(companyRoute(company_id!, "/"));
          }}
          className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      <form
        onSubmit={submitSale}
        className={`${A.card} max-w-3xl w-full p-6 md:p-8 ${
          showCustomerInlay ? "pointer-events-none" : ""
        }`}
        aria-hidden={showCustomerInlay}
      >
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
          <div className="w-full lg:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">{t("sales.common.customer")}</label>

            <div className="flex flex-col w-full gap-2">
              <div className="flex items-center w-full gap-2 min-w-0">
                <div className="flex-1 min-w-0 relative">
                  <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  preserveQueryOnDisplayChange
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                    key={customerComboKey}
                    className="w-full"
                    options={customers}
                    value={selectedCustomer}
                    onChange={handleCustomerChange}
                    getOptionLabel={(opt) => opt.name}
                    placeholder={t("sales.entry.selectCustomer")}
                  />
                </div>

                <button
                  type="button"
                  title={t("directory.customers.new")}
                  aria-label={t("directory.customers.new")}
                  className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 transition shrink-0`}
                  onClick={openCustomerInlay}
                  tabIndex={-1}
                >
                  <PlusIcon className="w-4 h-4 text-appleblue" />
                </button>
              </div>

              {hasCustomerContact && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {customerMobile && (
                    <input
                      type="text"
                      className={`${A.inputBase} bg-gray-50 text-gray-500 cursor-not-allowed focus:ring-0 focus:border-gray-200`}
                      value={customerMobile}
                      readOnly
                      placeholder={t("directory.common.mobile")}
                      tabIndex={-1}
                    />
                  )}

                  {customerEmailDisplay && (
                    <input
                      type="email"
                      className={`${A.inputBase} bg-gray-50 text-gray-500 cursor-not-allowed focus:ring-0 focus:border-gray-200`}
                      value={customerEmailDisplay}
                      readOnly
                      placeholder={t("directory.common.email")}
                      tabIndex={-1}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="w-full">
            <label className="block text-xs text-gray-500 mb-1">{t("sales.common.date")}</label>

            <AppleDateInput openCalendarLabel={t("sales.shared.openCalendar")}               valueYmd={date}
              onChangeYmd={setDate}
              required
              ariaLabel={t("sales.entry.invoiceDate")}
              inputClassName={`${A.inputBase} appearance-none placeholder-gray-400`}
              placeholder="MM/DD/YYYY"
            />
            {isClosedDate && (
              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                {t("sales.errors.closedWarning")}
              </div>
            )}
          </div>
        </div>

        <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50 p-3">
          <label className="block text-xs text-gray-500 mb-1">
            {t("sales.entry.scan")}
          </label>

          <div className="flex gap-2">
            <input
              ref={scanInputRef}
              type="text"
              className={A.inputBase}
              value={scanCode}
              onChange={(e) => setScanCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleScanSubmit(scanCode);
                }
              }}
              placeholder={t("sales.entry.barcode")}
              autoComplete="off"
            />

            <button
              type="button"
              className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 transition shrink-0`}
              onClick={cameraOpen ? stopCameraScan : startCameraScan}
              title={cameraOpen ? t("sales.entry.stopCamera") : t("sales.entry.openCamera")}
            >
              {cameraOpen ? (
                <StopIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <CameraIcon className="w-5 h-5 text-appleblue" />
              )}
            </button>
          </div>

          {cameraOpen && (
            <div className="mt-3 overflow-hidden rounded-2xl border border-gray-200 bg-black">
              <video
                ref={videoRef}
                className="h-56 w-full object-cover"
                muted
                playsInline
              />
            </div>
          )}

          {cameraError && (
            <div className="mt-2 text-[13px] text-red-500">{messageText(cameraError)}</div>
          )}

          <div className="mt-1 text-xs text-gray-500">
            {t("sales.entry.scanAgain")}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl mb-4 p-4">
          <div className="hidden md:block">
            <table className="w-full table-fixed">
              <colgroup>
                <col />
                <col style={{ width: "4.25rem" }} />
                <col style={{ width: "5.75rem" }} />
                <col style={{ width: "4.25rem" }} />
                <col style={{ width: "7.75rem" }} />
                <col style={{ width: "5.25rem" }} />
              </colgroup>

              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left pb-2 text-xs text-gray-500 pl-4 pr-2">
                    {t("sales.common.itemService")}
                  </th>
                  <th className="text-right pb-2 text-xs text-gray-500 pl-1 pr-2">
                    {t("sales.common.quantity")}
                  </th>
                  <th className="text-right pb-2 text-xs text-gray-500 pl-1 pr-2">
                    {t("sales.common.price")}
                  </th>
                  <th className="text-right pb-2 text-xs text-gray-500 pl-1 pr-2">
                    {t("sales.common.discountPercent")}
                  </th>
                  <th className="text-right pb-2 text-xs text-gray-500 pl-1 pr-2">
                    {t("sales.common.total")}
                  </th>
                  <th className="pb-2 pl-1 pr-1" />
                </tr>
              </thead>

              <tbody>
                {lines.map((line, i) => (
                  <React.Fragment key={i}>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 pl-4 pr-2 align-top overflow-visible">
                        <div className="relative min-w-0 w-full">
                          <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  preserveQueryOnDisplayChange
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                            className="w-full"
                            label=""
                            options={itemOptions}
                            value={getSelectedItemOption(line)}
                            onChange={(product) => handleItemChange(i, product)}
                            getOptionLabel={displayProductOptionLabel}
                            getOptionSearchText={(opt) =>
                              `${getProductName(opt)} ${getProductSku(opt)}`
                            }
                            filterOptions={filterProductOptions}
                            renderOption={(product) => renderProductOption(product, displayProductOptionLabel(product), t("sales.entry.sku", { sku: getProductSku(product) }))}
                            placeholder={t("sales.entry.selectItem")}
                          />
                        </div>
                      </td>

                      <td className="py-2 pl-1 pr-2 align-top">
                        <AppleNumberInput
                          className="px-2 text-right shadow-none appearance-none"
                          min={1}
                          step="1"
                          value={line.qty}
                          onChange={(e) =>
                            handleLineChange(i, "qty", e.target.value)
                          }
                          onBlur={(e) => handleBlur(i, "qty", e.target.value)}
                          autoComplete="off"
                        />
                      </td>

                      <td className="py-2 pl-1 pr-2 align-top">
                        <AppleNumberInput
                          className="px-2 text-right shadow-none appearance-none"
                          min={0}
                          step="0.01"
                          value={line.price}
                          onChange={(e) =>
                            handleLineChange(i, "price", e.target.value)
                          }
                          onBlur={(e) => handleBlur(i, "price", e.target.value)}
                          autoComplete="off"
                        />
                      </td>

                      <td className="py-2 pl-1 pr-2 align-top">
                        <AppleNumberInput
                          min={0}
                          max={100}
                          step="any"
                          className="px-2 text-right shadow-none appearance-none"
                          value={line.discount}
                          onChange={(e) =>
                            handleLineChange(i, "discount", e.target.value)
                          }
                          onBlur={(e) =>
                            handleBlur(i, "discount", e.target.value)
                          }
                          autoComplete="off"
                          placeholder="0"
                        />
                      </td>

                      <td className="py-2 pl-1 pr-2 align-top">
                        <AppleNumberInput
                          className="px-2 text-right shadow-none appearance-none"
                          min={0}
                          step="0.01"
                          value={line.total}
                          onChange={(e) =>
                            handleLineChange(i, "total", e.target.value)
                          }
                          onBlur={(e) => handleBlur(i, "total", e.target.value)}
                          autoComplete="off"
                          readOnly={false}
                        />
                      </td>

                      <td className="py-2 pl-1 pr-1 align-top">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title={t("directory.products.addNew")}
                            className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 transition shrink-0`}
                            onClick={() => setProductInlayIndex(i)}
                            tabIndex={-1}
                          >
                            <PlusIcon className="w-4 h-4 text-appleblue" />
                          </button>

                          <button
                            type="button"
                            className={`${
                              A.iconBtn
                            } inline-flex items-center justify-center transition shrink-0 ${
                              lines.length === 1
                                ? "cursor-not-allowed hover:bg-transparent"
                                : "hover:bg-gray-100"
                            }`}
                            onClick={() => removeLine(i)}
                            disabled={lines.length === 1}
                            title={t("sales.common.remove")}
                          >
                            <TrashIcon
                              className={`w-5 h-5 ${
                                lines.length === 1
                                  ? "text-gray-300"
                                  : "text-appleblue"
                              }`}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {line.product_id === "" && line.item_kind === "service" && (
                      <tr className="border-b border-gray-100">
                        <td colSpan={6} className="pb-2 pl-4 pr-2">
                          <input
                            type="text"
                            className={A.inputBase}
                            placeholder={t("sales.entry.serviceDescriptionDesktop")}
                            value={line.detail}
                            onChange={(e) =>
                              handleLineChange(i, "detail", e.target.value)
                            }
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden">
            {lines.map((line, i) => (
              <React.Fragment key={i}>
                <div className="flex flex-col gap-3 mb-3 rounded-2xl border border-gray-200 bg-white p-3">
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("sales.common.itemService")}
                    </label>
                    <div className="min-w-0">
                      <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  preserveQueryOnDisplayChange
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                        className="w-full"
                        label=""
                        options={itemOptions}
                        value={getSelectedItemOption(line)}
                        onChange={(product) => handleItemChange(i, product)}
                        getOptionLabel={displayProductOptionLabel}
                        getOptionSearchText={(opt) =>
                          `${getProductName(opt)} ${getProductSku(opt)}`
                        }
                        filterOptions={filterProductOptions}
                        renderOption={(product) => renderProductOption(product, displayProductOptionLabel(product), t("sales.entry.sku", { sku: getProductSku(product) }))}
                        placeholder={t("sales.entry.selectItem")}
                      />

                      {line.product_id === "" &&
                        line.item_kind === "service" && (
                        <div className="mt-2">
                          <input
                            type="text"
                            className={A.inputBase}
                            placeholder={t("sales.entry.serviceDescriptionMobile")}
                            value={line.detail}
                            onChange={(e) =>
                              handleLineChange(i, "detail", e.target.value)
                            }
                          />
                        </div>
                      )}

                      <div className="mt-2 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          title={t("directory.products.addNew")}
                          aria-label={t("directory.products.addNew")}
                          className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 transition shrink-0`}
                          onClick={() => setProductInlayIndex(i)}
                          tabIndex={-1}
                        >
                          <PlusIcon className="w-4 h-4 text-appleblue" />
                        </button>

                        <button
                          type="button"
                          className={`${
                            A.iconBtn
                          } inline-flex items-center justify-center transition shrink-0 ${
                            lines.length === 1
                              ? "cursor-not-allowed hover:bg-transparent"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => removeLine(i)}
                          disabled={lines.length === 1}
                          title={t("sales.common.remove")}
                          aria-label={t("sales.common.remove")}
                        >
                          <TrashIcon
                            className={`w-5 h-5 ${
                              lines.length === 1
                                ? "text-gray-300"
                                : "text-appleblue"
                            }`}
                          />
                        </button>
                      </div>
                    </div>{" "}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        {t("sales.common.quantity")}
                      </label>
                      <AppleNumberInput
                        className="px-2 text-right shadow-none appearance-none"
                        min={1}
                        step="1"
                        value={line.qty}
                        onChange={(e) =>
                          handleLineChange(i, "qty", e.target.value)
                        }
                        onBlur={(e) => handleBlur(i, "qty", e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        {t("sales.common.price")}
                      </label>
                      <AppleNumberInput
                        className="px-2 text-right shadow-none appearance-none"
                        min={0}
                        step="0.01"
                        value={line.price}
                        onChange={(e) =>
                          handleLineChange(i, "price", e.target.value)
                        }
                        onBlur={(e) => handleBlur(i, "price", e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        {t("sales.common.discountPercent")}
                      </label>
                      <AppleNumberInput
                        className="px-2 text-right shadow-none appearance-none"
                        min={0}
                        max={100}
                        step="any"
                        value={line.discount}
                        onChange={(e) =>
                          handleLineChange(i, "discount", e.target.value)
                        }
                        onBlur={(e) =>
                          handleBlur(i, "discount", e.target.value)
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        {t("sales.common.total")}
                      </label>
                      <AppleNumberInput
                        className="px-2 text-right shadow-none appearance-none"
                        min={0}
                        step="0.01"
                        value={line.total}
                        onChange={(e) =>
                          handleLineChange(i, "total", e.target.value)
                        }
                        onBlur={(e) => handleBlur(i, "total", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-3 px-1">
            <button
              type="button"
              className="text-[13px] font-normal text-appleblue bg-transparent border-none shadow-none px-0 py-0 hover:text-appleblueD focus:outline-none transition-colors"
              onClick={addLine}
              style={{ alignSelf: "flex-start" }}
            >
              {t("sales.entry.addItem")}
            </button>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 mb-4 flex justify-end">
          <div className="text-right">
            <div className="text-xs text-gray-500">{t("sales.common.grandTotal")}</div>
            <div className="text-[18px] font-semibold text-gray-900 tabular-nums">
              {formatMoney(grandTotal)}
            </div>
          </div>
        </div>

        <div className="w-full mb-4 pt-3 border-t border-gray-100">
          <div className={`${A.label} mb-2`}>{t("sales.common.payment")}</div>

          <label className="block text-xs text-gray-500 mb-1">
            {t("sales.common.paymentMethod")}
          </label>

          {(() => {
            const pmOptions = [
              ...displayMethods.map((pm) => {
                const parts: string[] = [];
                const pct = paymentMethodFeePercent(pm);
                if (pct !== 0) {
                  parts.push(`${pct.toFixed(2)}%`);
                }
                if (typeof pm.fixed_fee === "number" && pm.fixed_fee !== 0) {
                  parts.push(`$${pm.fixed_fee.toFixed(2)}`);
                }
                const feeTag = parts.length ? ` (${parts.join(" + ")})` : "";
                return { id: String(pm.id), label: `${pm.name}${feeTag}` };
              }),
              ...(hasStripe
                ? [{ id: STRIPE_SYNTH, label: "Stripe (online)" }]
                : []),
            ];

            const selectedOpt =
              pmOptions.find((o) => o.id === String(paymentMethodId || "")) ||
              null;

            return (
              <AppleComboBox
                  emptyText={t("sales.shared.noResults")}
                  preserveQueryOnDisplayChange
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                className="w-full"
                options={pmOptions}
                value={selectedOpt}
                onChange={(opt) => handlePaymentMethodSelect(opt?.id || "")}
                getOptionSearchText={(opt) => opt.label}
                getOptionLabel={(opt) => opt.id === "" ? t("sales.entry.selectMethod") : opt.id === STRIPE_SYNTH ? t("sales.entry.stripeOnline") : opt.label}
                placeholder={t("sales.entry.selectMethod")}
              />
            );
          })()}

          {stripeSelected && (
            <>
              <div className="mt-1 text-xs text-gray-500">
                {t("sales.entry.stripeWallets")}
              </div>

              <div className="mt-3">
                <div className="text-xs text-gray-500 mb-1">
                  {t("sales.entry.collectionMethod")}
                </div>

                <div className="inline-flex rounded-xl border border-gray-200 p-1 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setStripeCollectMode("now")}
                    className={`h-8 px-3 rounded-xl text-[13px] font-medium focus:outline-none focus:ring-2 focus:ring-appleblueD transition-colors ${
                      stripeCollectMode === "now"
                        ? "bg-white ring-1 ring-gray-200 text-gray-700"
                        : "text-gray-600 hover:text-gray-700"
                    }`}
                  >
                    {t("sales.entry.stripePayNow")}
                  </button>

                  <button
                    type="button"
                    onClick={() => setStripeCollectMode("link")}
                    className={`h-8 px-3 rounded-xl text-[13px] font-medium focus:outline-none focus:ring-2 focus:ring-appleblueD transition-colors ${
                      stripeCollectMode === "link"
                        ? "bg-white ring-1 ring-gray-200 text-gray-700"
                        : "text-gray-600 hover:text-gray-700"
                    }`}
                  >
                    {t("sales.entry.sendStripeLink")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {isOnAccount && (
          <div className="w-full mb-2 mt-3">
            <label className="block text-xs text-gray-500 mb-1">
              {t("sales.common.dueDate")}
            </label>
            <AppleDateInput openCalendarLabel={t("sales.shared.openCalendar")}               valueYmd={dueDate}
              onChangeYmd={onDueDateChange}
              minYmd={minDueYmd}
              allowEmpty
              ariaLabel={t("sales.common.dueDateAria")}
            />
            <p className="mt-1 text-[11px] text-gray-400">
              {t("sales.entry.dueHelp")}
            </p>
          </div>
        )}

        {paymentMethodId && !isOnAccount && (
          <div
            className={`grid grid-cols-1 ${
              stripeSelected || cashSelected ? "md:grid-cols-1" : "md:grid-cols-3"
            } gap-2 mt-3 mb-4`}
          >
            {!cashSelected && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {stripeSelected ? t("sales.entry.amountToCharge") : t("sales.common.amountPaid")}
                </label>
                <AppleNumberInput
                  min={0}
                  step="0.01"
                  className="px-2 text-right shadow-none appearance-none"
                  value={amountPaid}
                  onChange={(e) => {
                    amountPaidTouchedRef.current = true;
                    // FEE-RATE-SEMANTICS-1 Phase 2B: a genuine user amount change
                    // resets to configured and releases any restored explicit
                    // lock (matches current recalculation behavior).
                    feeIntentStateRef.current = reduceOnUserAmountOrMethodChange(
                      feeIntentStateRef.current,
                    );
                    setAmountPaid(e.target.value);
                  }}
                  onBlur={() => {
                    if (!amountPaid || amountPaid.trim() === "") return;

                    const paid = parseMoneyInput(amountPaid);
                    const max = grandTotal;

                    if (paid > max) {
                      setAmountPaid(toInputMoney(max));
                      return;
                    }

                    if (paid < 0) {
                      setAmountPaid("0");
                      return;
                    }

                    setAmountPaid(toInputMoney(paid));
                  }}
                  max={grandTotal.toFixed(2)}
                  placeholder={
                    stripeSelected ? t("sales.entry.leaveBlankCharge") : "0.00"
                  }
                />

                {stripeSelected && (
                  <div className="text-xs text-gray-500 mt-1">
                    {t("sales.entry.partialChargeHelp", { amount: formatMoney(grandTotal) })}
                  </div>
                )}
              </div>
            )}

            {overpayError && (
              <div
                className={`${
                  stripeSelected ? "md:col-span-1" : "md:col-span-3"
                } text-red-500 text-[13px] font-medium mt-1`}
              >
                {messageText(overpayError)}
              </div>
            )}

            {!stripeSelected && (
              <>
                {!cashSelected && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("sales.entry.feeWithRate", { rate: (() => {
                        const pm = paymentMethods.find(
                          (p) => String(p.id) === paymentMethodId,
                        );
                        const parts: string[] = [];
                        const pct = paymentMethodFeePercent(pm);
                        if (pct !== 0) {
                          parts.push(`${pct.toFixed(2)}%`);
                        }
                        if (
                          typeof pm?.fixed_fee === "number" &&
                          pm.fixed_fee !== 0
                        ) {
                          parts.push(`$${pm.fixed_fee.toFixed(2)}`);
                        }
                        return parts.join(" + ") || "0";
                      })() })}
                    </label>

                    <AppleNumberInput
                      min={0}
                      step="0.01"
                      className="px-2 text-right shadow-none appearance-none"
                      value={fee}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFee(v);
                        // FEE-RATE-SEMANTICS-1 Phase 2B: a user Fee edit is
                        // explicit intent (a typed value, incl. 0); clearing it
                        // returns to configured. This releases any restored
                        // explicit lock (a genuine user action).
                        feeIntentStateRef.current = reduceOnUserFeeEdit(
                          feeIntentStateRef.current,
                          v.trim() !== "",
                        );
                        const amt = parseMoneyInput(amountPaid);
                        const f = parseMoneyInput(v);
                        setNetAmount(toInputMoney(amt - f));
                      }}
                      placeholder="0.00"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    {t("sales.entry.netReceived")}
                  </label>
                  <input
                    type="number"
                    className={`${A.inputBase} bg-gray-50 text-right ${A.num}`}
                    value={netAmount}
                    readOnly
                    tabIndex={-1}
                    placeholder="0.00"
                  />
                </div>
              </>
            )}
          </div>
        )}

        <MemoSection label={t("sales.common.memo")} placeholder={t("sales.common.memoPlaceholder")}
          editAriaLabel={t("sales.common.editMemo")}
          mode="edit"
          value={memo}
          onChange={setMemo}
          className="mt-4 mb-8"
        />

        <div className="flex flex-col mb-4">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="sendEmailNow"
              className="mt-[3px] mr-2 h-4 w-4 rounded border-gray-300 accent-appleblue focus:ring-2 focus:ring-appleblueD"
              checked={sendEmailNow && canSendInvoiceNow}
              onChange={(e) => {
                if (!canSendInvoiceNow) return;
                setSendEmailNow(e.target.checked);
              }}
              disabled={!canSendInvoiceNow}
            />

            <div
              className="flex flex-col"
              onClick={() => {
                if (isPro || !selectedCustomer?.id) return;

                posthog.capture("invoice_auto_send_blocked", {
                  company_id,
                  source: "sale_entry",
                  trigger_type: "auto",
                  user_plan: "basic",
                  block_reason: "pro_required",
                  entry_point: "checkbox_label",
                });

                setUpgradeOpen(true);
              }}
            >
              <label
                htmlFor="sendEmailNow"
                className={`text-[13px] ${
                  canSendInvoiceNow ? "text-gray-700" : "text-gray-400"
                } ${!isPro && selectedCustomer?.id ? "cursor-pointer" : ""}`}
              >
                {t("sales.entry.sendNow")}
              </label>

              <div
                className={`mt-1 text-xs ${
                  canSendInvoiceNow ? "text-gray-500" : "text-gray-400"
                } ${!isPro && selectedCustomer?.id ? "cursor-pointer" : ""}`}
              >
                {t("sales.entry.sendingSequence")}
              </div>

              {!isPro && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();

                    posthog.capture("pro_upgrade_clicked", {
                      company_id,
                      source: "sale_entry",
                      trigger_reason: "invoice_auto_send",
                      user_plan: "basic",
                      entry_point: "upgrade_button",
                    });

                    setUpgradeOpen(true);
                  }}
                  className="mt-1 text-xs text-appleblue hover:text-appleblueD font-medium self-start"
                >
                  {t("sales.entry.upgradePro")}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              clearDraft(SALE_DRAFT_KEY);
              resetSaleEntry();
              navigate(companyRoute(company_id!, "/dashboard"), {
                replace: true,
              });
            }}
            disabled={saving}
          >
            {t("directory.common.cancel")}
          </Button>

          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? t("directory.common.savingDots") : t("sales.common.enter")}
          </Button>
        </div>

        {error && (
          <div className="text-red-500 text-[13px] font-medium mt-2">
            {messageText(error)}
          </div>
        )}
      </form>

      {showStripeQrModal && (
        <div
          className="fixed inset-0 z-[1100] bg-black/40 p-3 sm:p-4 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
        >
          <div
            className={`${A.card} w-full max-w-lg max-h-[90vh] overflow-auto p-0 shadow-md`}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className={A.title}>{t("sales.common.scanToPay")}</h3>

              <button
                type="button"
                aria-label={t("directory.common.close")}
                onClick={finishAndExit}
                className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100`}
              >
                <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div className="text-[13px] text-gray-600">
                {t("sales.common.scanInstructions")}
              </div>

              <div className="flex justify-center">
                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  {stripeCheckoutUrl ? (
                    <QRCodeSVG value={stripeCheckoutUrl} size={220} />
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={handleCopyCheckoutLink}
                  disabled={!stripeCheckoutUrl}
                >
                  {copiedCheckoutUrl ? t("sales.common.copied") : t("sales.common.copyLink")}
                </Button>

                <div className="text-xs text-gray-500 text-center">
                  {t("sales.common.noCustomerPhone")}
                </div>

                {stripeCheckoutUrl && (
                  <a
                    href={stripeCheckoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${A.btnBase} ${A.btnSecondary} w-full`}
                    style={{ minWidth: "120px" }}
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      <span className="whitespace-nowrap">{t("sales.common.enterCard")}</span>
                    </span>
                  </a>
                )}

                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  onClick={finishAndExit}
                >
                  {t("sales.common.done")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCustomerInlay && (
        <CustomerEntry
          mode="inlay"
          title={t("directory.customers.add")}
          variant="full"
          companyId={company_id}
          onCreated={(c) => {
            const adapted: Customer = {
              id: (c as any).id,
              name: (c as any).name,
              email: (c as any).email ?? undefined,
              mobile: (c as any).mobile ?? undefined,
              phone: (c as any).phone ?? undefined,
            };
            setCustomers((prev) => [...prev, adapted]);
            setSelectedCustomer(adapted);
            setCustomerEmail(adapted.email ?? "");
            setCustomerComboKey((k) => k + 1);
            setShowCustomerInlay(false);
          }}
          onClose={() => {
            setCustomerComboKey((k) => k + 1);
            setShowCustomerInlay(false);
          }}
        />
      )}

      {productInlayIndex !== null && (
        <ProductEntry
          mode="inlay"
          title={t("directory.products.add")}
          variant="full"
          companyId={company_id}
          onCreated={(p) => {
            const adapted: Product = {
              id: (p as any).id,
              name: (p as any).name,
              sell_price: Number((p as any).sell_price ?? 0),
            };
            setProducts((prev) => [...prev, adapted]);

            const priceStr = String(
              Math.round(Number((p as any).sell_price ?? 0) * 100) / 100,
            );
            setLines((prev) =>
              prev.map((ln, idx) =>
                idx === productInlayIndex
                  ? {
                      ...ln,
                      product_id: String((p as any).id),
                      item_kind: (p as any).is_service ? "service" : "product",
                      detail: "",
                      qty: "1",
                      price: priceStr,
                      discount: "0",
                      total: getLineTotal("1", priceStr, "0"),
                      lastEdited: undefined,
                    }
                  : ln,
              ),
            );
          }}
          onClose={() => setProductInlayIndex(null)}
        />
      )}

      <UpgradeModal closeLabel={t("directory.common.close")} labels={{
            title: t("sales.upgrade.title"),
            price: t("sales.upgrade.price"),
            description: t("sales.upgrade.description"),
            staff: <Trans i18nKey="sales.upgrade.staff" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />,
            emails: <Trans i18nKey="sales.upgrade.emails" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />,
            whatsapp: t("sales.upgrade.whatsapp"),
            branding: t("sales.upgrade.branding"),
            receiptScanner: <Trans i18nKey="sales.upgrade.receiptScanner" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />,
            embeddedLinks: <Trans i18nKey="sales.upgrade.embeddedLinks" components={{ emphasis0: <span className="font-medium text-gray-700" />, emphasis1: <span className="font-medium text-gray-600" /> }} />,
            fees: t("sales.upgrade.fees"),
            gatewayHelp: <Trans i18nKey="sales.upgrade.gatewayHelp" components={{ emphasis0: <span className="font-medium text-gray-600" />, emphasis1: <span className="font-medium text-gray-600" /> }} />,
            enterCode: t("sales.upgrade.enterCode"),
            invitationCode: t("sales.upgrade.invitationCode"),
            notNow: t("sales.upgrade.notNow"),
            opening: t("sales.upgrade.opening"),
            startPro: t("sales.upgrade.startPro"),
            alreadyPro: t("sales.upgrade.alreadyPro"),
            billingPortal: t("sales.upgrade.billingPortal"),
            secure: t("sales.upgrade.secure"),
            timeout: t("sales.upgrade.timeout"),
            activating: t("sales.upgrade.activating"),
            active: t("sales.upgrade.active"),
            used: t("sales.upgrade.used"),
            ineligible: t("sales.upgrade.ineligible"),
            invalidCode: t("sales.upgrade.invalidCode"),
            startTrialFailed: t("sales.upgrade.startTrialFailed"),
            checkoutFailed: t("sales.upgrade.checkoutFailed"),
            missingPortalUrl: t("sales.upgrade.missingPortalUrl"),
            portalFailed: t("sales.upgrade.portalFailed"),
            dismiss: t("sales.upgrade.dismiss")
          }} open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}
